import { TbTrash } from 'react-icons/tb';
import {
    AccordionItem,
    AccordionPanel,
    Stack,
    Box,
    Badge,
    Checkbox,
    Flex,
    IconButton,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
} from '@chakra-ui/react';
import { Button, Input, LoadingIndicator, Tooltip } from '@/components/v4';
import { textBoxStyling } from 'components/forms/new/formStyles';
import RingGroupAccordionSectionButton from './RingGroupAccordionSectionButton';

export default function RingGroupMembersSection({
    memberSearch,
    setMemberSearch,
    isLoadingMembers,
    isLoadingCandidates,
    hasEligibleMembersWithPhoneNumber,
    allCheckboxRowsChecked,
    someCheckboxRowsChecked,
    checkboxRows,
    toggleAllPending,
    isSubmitting,
    memberRows,
    isSearchResultEmpty,
    memberPage,
    totalMemberPages,
    onPrevMemberPage,
    onNextMemberPage,
    toggleMemberPending,
    setVoicemailOverride,
    setDeleteConfirmId,
    darkMode,
    panelBg,
    accordionBorderColor,
}) {
    return (
        <AccordionItem
            borderColor={accordionBorderColor}
            mb={2}
            borderWidth="1px"
            borderRadius="md"
            overflow="hidden">
            <RingGroupAccordionSectionButton darkMode={darkMode}>
                Members
            </RingGroupAccordionSectionButton>
            <AccordionPanel px={4} py={4} bg={panelBg}>
                {isLoadingMembers || isLoadingCandidates ? (
                    <Flex
                        align="center"
                        justify="center"
                        minH="120px"
                        data-testid="members-loading-indicator">
                        <LoadingIndicator sx={{ transform: 'scale(2)' }} />
                    </Flex>
                ) : !hasEligibleMembersWithPhoneNumber ? (
                    <Text fontSize="sm" color="gray.500">
                        No members are available
                    </Text>
                ) : (
                    <Stack spacing={3}>
                        <Input
                            placeholder="Search by name or number..."
                            value={memberSearch}
                            onChange={(e) => setMemberSearch(e.target.value)}
                            sx={textBoxStyling}
                        />

                        <Box
                            borderWidth="1px"
                            borderColor={accordionBorderColor}
                            borderRadius="md"
                            overflow="hidden"
                            maxH="320px"
                            overflowY="auto">
                            <Table size="sm">
                                <Thead>
                                    <Tr
                                        sx={{
                                            th: { textTransform: 'capitalize' },
                                        }}>
                                        <Th>
                                            <Checkbox
                                                isChecked={
                                                    allCheckboxRowsChecked
                                                }
                                                isIndeterminate={
                                                    someCheckboxRowsChecked &&
                                                    !allCheckboxRowsChecked
                                                }
                                                isDisabled={
                                                    checkboxRows.length === 0 ||
                                                    isSubmitting
                                                }
                                                onChange={toggleAllPending}
                                            />
                                        </Th>
                                        <Th>Name</Th>
                                        <Th>Phone Number</Th>
                                        <Th>Extension</Th>
                                        <Th>Voicemail</Th>
                                        <Th />
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {isSearchResultEmpty ? (
                                        <Tr>
                                            <Td colSpan={6}>
                                                <Text
                                                    fontSize="sm"
                                                    color="gray.500">
                                                    No eligible 8x8 users found
                                                </Text>
                                            </Td>
                                        </Tr>
                                    ) : (
                                        memberRows.map((row) => {
                                            const showActionMenu =
                                                row.committedIsMember &&
                                                !row.isPending;
                                            return (
                                                <Tr key={row.id}>
                                                    <Td>
                                                        {!showActionMenu && (
                                                            <Checkbox
                                                                isChecked={
                                                                    row.isMember
                                                                }
                                                                isDisabled={
                                                                    isSubmitting
                                                                }
                                                                onChange={() =>
                                                                    toggleMemberPending(
                                                                        row.id,
                                                                    )
                                                                }
                                                            />
                                                        )}
                                                    </Td>
                                                    <Td>
                                                        {row.name}
                                                        {row.isPending ? (
                                                            <Badge
                                                                colorScheme="yellow"
                                                                fontSize="9px"
                                                                ml={2}
                                                                verticalAlign="middle">
                                                                Pending
                                                            </Badge>
                                                        ) : (
                                                            row.committedIsMember && (
                                                                <Badge
                                                                    colorScheme="green"
                                                                    fontSize="9px"
                                                                    ml={2}
                                                                    verticalAlign="middle">
                                                                    Added
                                                                </Badge>
                                                            )
                                                        )}
                                                    </Td>
                                                    <Td>{row.phoneNumber}</Td>
                                                    <Td>{row.extension}</Td>
                                                    <Td>
                                                        {showActionMenu ? (
                                                            <Checkbox
                                                                isChecked={
                                                                    row.voicemailAccess
                                                                }
                                                                isDisabled={
                                                                    isSubmitting
                                                                }
                                                                onChange={(e) =>
                                                                    setVoicemailOverride(
                                                                        row.id,
                                                                        e.target
                                                                            .checked,
                                                                    )
                                                                }
                                                            />
                                                        ) : (
                                                            '—'
                                                        )}
                                                    </Td>
                                                    <Td textAlign="right">
                                                        {showActionMenu && (
                                                            <Tooltip label="Delete">
                                                                <IconButton
                                                                    icon={
                                                                        <TbTrash />
                                                                    }
                                                                    aria-label="Delete"
                                                                    size="sm"
                                                                    variant="ghost"
                                                                    colorScheme="red"
                                                                    isDisabled={
                                                                        isSubmitting
                                                                    }
                                                                    onClick={() =>
                                                                        setDeleteConfirmId(
                                                                            row.id,
                                                                        )
                                                                    }
                                                                />
                                                            </Tooltip>
                                                        )}
                                                    </Td>
                                                </Tr>
                                            );
                                        })
                                    )}
                                </Tbody>
                            </Table>
                        </Box>

                        {!isSearchResultEmpty && (
                            <Flex justify="flex-end" align="center" gap={3}>
                                <Text fontSize="xs" color="gray.500">
                                    Page {memberPage} of {totalMemberPages}
                                </Text>
                                <Button
                                    variant="outline"
                                    size="xs"
                                    onClick={onPrevMemberPage}
                                    isDisabled={memberPage <= 1}>
                                    Previous
                                </Button>
                                <Button
                                    variant="outline"
                                    size="xs"
                                    onClick={onNextMemberPage}
                                    isDisabled={memberPage >= totalMemberPages}>
                                    Next
                                </Button>
                            </Flex>
                        )}
                    </Stack>
                )}
            </AccordionPanel>
        </AccordionItem>
    );
}
